// components/LoadingStates.js
import React from 'react';

/**
 * Skeleton loading component for brand cards
 */
export const BrandCardSkeleton = () => (
  <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200 animate-pulse">
    <div className="h-48 bg-gray-300"></div>
    <div className="p-4 space-y-3">
      <div className="h-4 bg-gray-300 rounded w-3/4"></div>
      <div className="h-3 bg-gray-300 rounded w-1/2"></div>
      <div className="h-12 bg-gray-300 rounded"></div>
    </div>
  </div>
);

/**
 * Grid of skeleton loading cards
 */
export const BrandGridSkeleton = ({ count = 8 }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
    {[...Array(count)].map((_, i) => (
      <BrandCardSkeleton key={i} />
    ))}
  </div>
);

/**
 * Loading spinner component
 */
export const LoadingSpinner = ({ size = 'md', className = '' }) => {
  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-8 w-8',
    lg: 'h-12 w-12'
  };

  return (
    <div className={`animate-spin rounded-full border-t-2 border-b-2 border-blue-600 ${sizeClasses[size]} ${className}`}></div>
  );
};

/**
 * Loading progress bar component
 */
export const LoadingProgressBar = ({ progress = 0, message = 'Loading...', showPercentage = true }) => {
  return (
    <div className="w-full max-w-md mx-auto p-4">
      <div className="mb-2 flex justify-between items-center">
        <span className="text-sm font-medium text-gray-700">{message}</span>
        {showPercentage && (
          <span className="text-sm text-gray-500">{Math.round(progress)}%</span>
        )}
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2.5">
        <div 
          className="bg-blue-600 h-2.5 rounded-full transition-all duration-300 ease-out"
          style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
        ></div>
      </div>
    </div>
  );
};

/**
 * Smooth animated loading progress bar (1% to 100% over 10 seconds)
 */
export const AnimatedLoadingBar = ({ message = 'Loading brands...', subMessage = 'Please wait while we fetch your data', progress = 0 }) => {
  return (
    <div className="w-full max-w-lg mx-auto p-6 text-center">
      <div className="mb-6">
        <h3 className="text-lg font-medium text-gray-900 mb-2">{message}</h3>
        <p className="text-sm text-gray-600">{subMessage}</p>
      </div>
      
      {/* Smooth progress bar */}
      <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden mb-4">
        <div 
          className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-300 ease-out"
          style={{ width: `${Math.max(1, Math.min(100, progress))}%` }}
        >
          <div className="h-full bg-gradient-to-r from-transparent via-white to-transparent opacity-20 animate-pulse"></div>
        </div>
      </div>
      
      {/* Progress percentage */}
      <div className="text-sm font-medium text-gray-700">
        {Math.round(Math.max(1, Math.min(100, progress)))}%
      </div>
    </div>
  );
};

/**
 * Loading state with message
 */
export const LoadingMessage = ({ message = 'Loading...', showSpinner = true }) => (
  <div className="flex justify-center items-center py-6">
    {showSpinner && <LoadingSpinner className="mr-2" />}
    <span className="text-gray-600">{message}</span>
  </div>
);

/**
 * Progressive loading container
 */
export const ProgressiveLoader = ({ 
  loadingState, 
  hasData, 
  children, 
  skeletonCount = 8,
  loadingMessage = 'Loading brands...',
  emptyMessage = 'No brands available'
}) => {
  // Show skeleton only when initially loading with no data
  if (loadingState === 'loading' && !hasData) {
    return (
      <div className="space-y-6">
        <BrandGridSkeleton count={skeletonCount} />
        <LoadingMessage message={loadingMessage} />
      </div>
    );
  }

  // Show content if we have data, regardless of loading state
  if (hasData) {
    return children;
  }

  // Show empty state
  return (
    <div className="bg-white rounded-lg shadow-md p-8 text-center">
      <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <p className="mt-4 text-gray-600">{emptyMessage}</p>
    </div>
  );
};

/**
 * Background refresh indicator
 */
export const BackgroundRefreshIndicator = ({ isRefreshing, lastUpdated }) => {
  if (!isRefreshing) return null;

  return (
    <>
      {/* Mobile version - positioned at top center */}
      <div className="fixed top-16 left-1/2 transform -translate-x-1/2 z-[60] bg-blue-500 text-white px-3 py-2 rounded-lg shadow-lg animate-fadeIn md:hidden">
        <div className="flex items-center">
          <LoadingSpinner size="sm" className="mr-2 border-white" />
          <span className="text-xs">Updating...</span>
        </div>
      </div>
      
      {/* Desktop version - positioned at top right */}
      <div className="hidden md:block fixed top-16 right-4 z-[60] bg-blue-500 text-white px-4 py-2 rounded-lg shadow-lg animate-fadeIn">
        <div className="flex items-center">
          <LoadingSpinner size="sm" className="mr-2 border-white" />
          <span className="text-sm">Updating...</span>
        </div>
      </div>
    </>
  );
};

/**
 * Cache status indicator
 */
export const CacheStatusIndicator = ({ 
  loadingState, 
  lastUpdated, 
  showCacheInfo = false 
}) => {
  if (!showCacheInfo) return null;

  const getStatusColor = () => {
    switch (loadingState) {
      case 'cached': return 'bg-yellow-50 border-yellow-500 text-yellow-700';
      case 'loaded': return 'bg-green-50 border-green-500 text-green-700';
      case 'loading': return 'bg-blue-50 border-blue-500 text-blue-700';
      case 'error': return 'bg-red-50 border-red-500 text-red-700';
      default: return 'bg-gray-50 border-gray-500 text-gray-700';
    }
  };

  const getStatusMessage = () => {
    switch (loadingState) {
      case 'cached': 
        return `Showing cached data from ${lastUpdated?.toLocaleTimeString() || 'unknown time'}`;
      case 'loaded': 
        return `Fresh data loaded at ${lastUpdated?.toLocaleTimeString() || 'now'}`;
      case 'loading': 
        return 'Loading fresh data...';
      case 'error': 
        return 'Error loading data, showing cached version';
      default: 
        return 'Unknown state';
    }
  };

  return (
    <div className={`border-l-4 p-3 mb-4 rounded-md ${getStatusColor()}`}>
      <p className="text-sm">{getStatusMessage()}</p>
    </div>
  );
};

/**
 * Error boundary for loading states
 */
export const LoadingErrorBoundary = ({ error, onRetry, children }) => {
  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-md mb-6">
        <div className="flex items-center">
          <svg className="h-5 w-5 text-red-400 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-red-700">{error}</p>
        </div>
        {onRetry && (
          <button
            onClick={onRetry}
            className="mt-2 bg-red-100 hover:bg-red-200 text-red-800 px-3 py-1 rounded text-sm transition-colors"
          >
            Try Again
          </button>
        )}
      </div>
    );
  }

  return children;
};

export default {
  BrandCardSkeleton,
  BrandGridSkeleton,
  LoadingSpinner,
  LoadingMessage,
  LoadingProgressBar,
  AnimatedLoadingBar,
  ProgressiveLoader,
  BackgroundRefreshIndicator,
  CacheStatusIndicator,
  LoadingErrorBoundary
};